import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { HumanType } from "../../types/feature";
import { FeaturesFormContext } from "../../contexts/features-form-context";
import { HumanFormType } from "../../types/forms";
import { SexEnum } from "../../types/sex";
import Button from "../ui/button";
import handleInputChange from "../../utils/handleInputChange";
import { toDateString } from "../../utils/date";
import UploadImage from "../ui/UploadImage";
import ProtectionForm from "./ProtectionForm";
import WarningForm from "./WarningForm";
import { API_DELETE_IMG, BASE_API } from "../../constants/api";
import useUploadImage from "../../hooks/useUploadImage";
import useRequest from "../../hooks/useRequest";

type HumanFormProps = {
  data?: HumanType | null;
}

const HumanForm = ({data}: HumanFormProps) => {
  const intl = useIntl();
  const {updateFeatureForm} = useContext(FeaturesFormContext);
  const [form, updateForm] = useState<HumanFormType>(() => initForm());
  const passHasBeenChanged = useRef(false);

  const [file, setFile] = useState<File | null>(null);
  const {upload, response: uploadRes} = useUploadImage();
  const {request: requestDelete} = useRequest();
  const deleteFile = useCallback((image: string) => {
    requestDelete(BASE_API, API_DELETE_IMG, {method: 'POST', secure: true}, {image});
  }, [requestDelete])
 
  useEffect(() => {
    if (data) {
      updateForm({
        ...data,
        language: data.language || 'fr'  // Ensure language has a default value
      })
    } else {
      updateForm(() => {return {...initForm()}})
    }
  }, [data])

  useEffect(() => {
    if (uploadRes) {
      const newImage = uploadRes.image;
      setFile(null);
      form.image && deleteFile(form.image)
      updateFeatureForm({
          ...form,
          image: newImage,
          password: passHasBeenChanged.current ? form.password : ''
        })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteFile, updateFeatureForm, uploadRes])

  
  function initForm(): HumanFormType {
    return {
      name: null,
      dateOfBirth: null,
      sex: SexEnum.Male,
      address: null,
      emergencyContact: null,
      school: null,
      image: null,
      physicalDescription: null,
      informationAdditional: null,
      isProtected: false,
      password: null,
      isWarning: false,
      warningMessage: null,
      language: "fr"
    };
  }

  function onsubmit() {
    if (!file) {
      updateFeatureForm({
        ...form,
        password: passHasBeenChanged.current ? form.password : ''
      })
    } else {
      upload(file);
    }
  }

  return (
    <>

      {/* Language Selection */}
      <div className="mb-5">
        <label className="mb-1.5 block text-shadow text-sm" htmlFor="language">
          {intl.formatMessage({ id: 'forms.human.displayLanguage' })}
        </label>
        <select 
          id="language" 
          name="language"
          className="cursor-pointer input-sm"
          value={form.language || 'fr'} 
          onChange={e => handleInputChange(e, updateForm)}
        >
          <option value="fr">{intl.formatMessage({ id: 'common.languageFrench' })}</option>
          <option value="en">{intl.formatMessage({ id: 'common.languageEnglish' })}</option>
          <option value="es">{intl.formatMessage({ id: 'common.languageSpanish' })}</option>
          <option value="pt">{intl.formatMessage({ id: 'common.languagePortuguese' })}</option>
          <option value="nl">{intl.formatMessage({ id: 'common.languageDutch' })}</option>
        </select>
      </div>

      {/* Nom complet */}
      <div className="mb-5">
        <label className="mb-1.5 block text-shadow text-sm" htmlFor="name">
          {intl.formatMessage({ id: 'forms.human.fullName' })}
        </label>
        <input id="name" type="text" name="name"
          placeholder={intl.formatMessage({ id: 'forms.human.fullNamePlaceholder' })}
          autoComplete='name'
          value={form.name || ''} onChange={e => handleInputChange(e, updateForm)}
          className="input-sm"
        />
      </div>

      {/* Photo */}
      <div className="mb-5">
        <label className="mb-1.5 block text-shadow text-sm" htmlFor="image">
          {intl.formatMessage({ id: 'forms.human.photo' })}
        </label>
        <UploadImage id="image" url={form.image} onChange={file => setFile(file)}
          fileValidation={{types: ['jpg', 'jpeg', 'png'], maxSize: 0.75}} />
      </div>
    
      {/* Date de Naissance */}
      <div className="mb-5">
        <label className="mb-1.5 block text-shadow text-sm" htmlFor="dateOfBirth">
          {intl.formatMessage({ id: 'forms.human.dateOfBirth' })}
        </label>
        <input className="input-sm" type="date" id="dateOfBirth" name="dateOfBirth"
          value={toDateString(form.dateOfBirth)} onChange={e => handleInputChange(e, updateForm)} />
      </div>
      
      {/* Sexe */}
      <div className="my-5 flex justify-between">
        <label className="block text-shadow text-sm">
          {intl.formatMessage({ id: 'forms.human.sex' })}
        </label>
        <div className="flex gap-7">
          <div className="flex flex-row-reverse w-fit gap-2">
            <input id="male" type="radio" name="sex"
              checked={form.sex === SexEnum.Male}
              value={SexEnum.Male} onChange={e => handleInputChange(e, updateForm)}
            />
            <label htmlFor="male" className="text-sm">
              {intl.formatMessage({ id: 'forms.human.male' })}
            </label>
          </div>
          <div className="flex flex-row-reverse w-fit gap-2">
            <input id="female" type="radio" name="sex"
              checked={form.sex === SexEnum.Female}
              value={SexEnum.Female} onChange={e => handleInputChange(e, updateForm)}
            />
            <label htmlFor="female" className="text-sm">
              {intl.formatMessage({ id: 'forms.human.female' })}
            </label>
          </div>
        </div>
      </div>

      {/* Adresse */}
      <div className="mb-5">
        <label className="mb-1.5 block text-shadow text-sm" htmlFor="address">
          {intl.formatMessage({ id: 'forms.human.address' })}
        </label>      
        <input id="address" type="text" name="address"
          placeholder={intl.formatMessage({ id: 'forms.human.addressPlaceholder' })}
          autoComplete="street-address" value={form.address || ''} onChange={e => handleInputChange(e, updateForm)}
          className="input-sm"
        />
      </div>

      {/* Contact d'Urgence */}
      <div className="mb-5">
        <label className="mb-1.5 block text-shadow text-sm" htmlFor="emergencyContact">
          {intl.formatMessage({ id: 'forms.human.emergencyContact' })}
        </label>
        <input id="emergencyContact" type="tel" name="emergencyContact"
          placeholder={intl.formatMessage({ id: 'forms.human.emergencyPlaceholder' })}
          autoComplete="tel"
          value={form.emergencyContact || ''} onChange={e => handleInputChange(e, updateForm)}
          className="input-sm"
        />
      </div>

      {/* Établissement */}
      <div className="mb-5">
        <label className="mb-1.5 block text-shadow text-sm" htmlFor="school">
          {intl.formatMessage({ id: 'forms.human.school' })}
        </label>      
        <input id="school" type="text" name="school"
          placeholder={intl.formatMessage({ id: 'forms.human.schoolPlaceholder' })}
          value={form.school || ''} onChange={e => handleInputChange(e, updateForm)}
          className="input-sm"
        />
      </div>

      {/* Description Physique */}
      <div className="mb-5">
        <label className="mb-1.5 block text-shadow text-sm" htmlFor="physicalDescription">
          {intl.formatMessage({ id: 'forms.human.physicalDescription' })}
        </label>
        <textarea name="physicalDescription" id="physicalDescription" rows={8}
          value={form.physicalDescription || ''} onChange={e => handleInputChange(e, updateForm)}
          className="input-sm"
        />
      </div>

      {/* Informations Supplémentaires */}
      <div className="mb-5">
        <label className="mb-1.5 block text-shadow text-sm" htmlFor="informationAdditional">
          {intl.formatMessage({ id: 'forms.human.additionalInfo' })}
        </label>
        <textarea name="informationAdditional" id="informationAdditional" rows={8}
          value={form.informationAdditional || ''} onChange={e => handleInputChange(e, updateForm)}
          className="input-sm"
        />
      </div>

      {/* Protection par Mot de passe */}
      <ProtectionForm
        isProtected={form.isProtected ?? false}
        password={form.password}
        toggle={isProtected => updateForm(prev => ({...prev, isProtected}))}
        change={text => {passHasBeenChanged.current = true; updateForm(prev => ({...prev, password: text}))}}
      />

      {/* Avertissement contenu sensible */}
      <WarningForm
        isWarning={form.isWarning ?? false}
        warningMessage={form.warningMessage}
        toggle={isWarning => updateForm(prev => ({...prev, isWarning}))}
        change={text => updateForm(prev => ({...prev, warningMessage: text}))}
      />
      
      <Button className="px-[27px] my-14 block" width="fit"
        onClick={() => onsubmit()}>
        {intl.formatMessage({ id: 'forms.human.submit' })}
      </Button>
    </>
  )
}

export default HumanForm;
